import { ObjectTools } from '../../tools/object-tools';
import { SolLayout } from './sol-layout';
import { SolText } from './sol-text';
import { SolType } from './sol-type';
import { SolViewId } from './sol-view-id';
import { SolViewOptions } from './sol-view-options';
import { SolViewData } from '../../components/view/sol-view/sol-view.component';
import { SolSelectValue } from './sol-select-value';
import { SolMenuGroup } from '../../components/popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { SelectTools } from '../../tools/select-tools';

export type SolSelectPresentation = 'dropdown' | 'embedded';

export type SolSelectMode = 'single' | 'multi';
export type SolSelectSearchMode = 'local' | 'remote';

export interface SolSelectSearchEvent {
  query: string;
  searchVersion: number;
}

export type SolSelectEdge = 'first' | 'last';

export class SolSelect<V> {
  private constructor(
    public readonly mode: SolSelectMode,
    public readonly searchMode: SolSelectSearchMode,
    public readonly query: string,
    public readonly dataType: SolType<V>,
    public readonly viewId: SolViewId<any> | undefined,
    public readonly viewOptions: Partial<SolViewOptions>,
    public readonly values: (string | undefined)[],
    public readonly availableValues: SolSelectValue<V>[],
    public readonly cachedValues: SolSelectValue<V>[],
    public readonly visibleAvailableValues: SolSelectValue<V>[],
    public readonly loading: boolean,
    public readonly debounceMs: number,
    public readonly minChars: number,
    public readonly disabled: boolean,
    public readonly placeholder: SolText,
    public readonly currentResultId: string | undefined,
    public readonly popoverVisible: boolean,
    public readonly selectedViewData: SolViewData<V[], SolViewOptions>,
    public readonly hasSelection: boolean,
    public readonly showClear: boolean,
    public readonly searchVersion: number,
    public readonly transparent: boolean,
    public readonly menu: SolMenuGroup[],
    public readonly listboxId: string | undefined,
    public readonly activeOptionId: string | undefined,
    public readonly searchableRequested: boolean,
    public readonly searchable: boolean,
    public readonly grow: boolean,
    public readonly hasError: boolean,
    public readonly editable: boolean,
    public readonly presentation: SolSelectPresentation,
  ) {}

  public static of<V>(dataType: SolType<V>): SolSelect<V> {
    const select = new SolSelect<V>(
      'single',
      'local',
      '',
      dataType,
      undefined,
      {},
      [undefined],
      [],
      [],
      [],
      false,
      0,
      2,
      false,
      SolText.ofRaw(''),
      undefined,
      false,
      {
        dataType: dataType.array,
        viewId: SolViewId.tag,
        mode: 'read',
        value: [],
        viewOptions: {},
      },
      false,
      false,
      0,
      false,
      [],
      undefined,
      undefined,
      true,
      true,
      true,
      false,
      false,
      'dropdown',
    );
    return select.calculateViewData();
  }

  /** `of()` plus the defaults the select's own scalar inputs are built on: no search field and a popover no wider than the trigger. */
  public static ofScalar<V>(dataType: SolType<V>, viewId: SolViewId<any>): SolSelect<V> {
    return SolSelect.of<V>(dataType).withViewId(viewId).withSearchable(false).withGrow(false);
  }

  public value(): string | undefined {
    return this.values.length === 0 ? undefined : this.values[0];
  }

  public clear(): SolSelect<V> {
    const clearedValues = this.mode === 'single' ? [undefined] : [];
    return this.withValues(clearedValues).withCurrentResultId(undefined);
  }

  public withPresentation(presentation: SolSelectPresentation): SolSelect<V> {
    return this.copy({ presentation, popoverVisible: presentation === 'dropdown' && this.popoverVisible });
  }

  public completesSelection(id: string | undefined): boolean {
    return this.presentation === 'embedded' && !this.disabled && id !== undefined && this.canSelect(id);
  }

  public withMode(mode: SolSelectMode): SolSelect<V> {
    const normalizedValues = SolSelect.normalizeValues(mode, this.values);
    return this.copy({ mode }).withValues(normalizedValues).recalculateCurrentResultId().recalculateActiveOptionId();
  }

  public withSearchMode(searchMode: SolSelectSearchMode): SolSelect<V> {
    return this.copy({ searchMode })
      .recalculateSearchable()
      .recalculateVisibleAvailableValues()
      .recalculateCurrentResultId()
      .recalculateActiveOptionId();
  }

  public withQuery(query: string): SolSelect<V> {
    return this.copy({ query })
      .recalculateVisibleAvailableValues()
      .recalculateCurrentResultId()
      .recalculateActiveOptionId();
  }

  public withViewId(viewId: SolViewId<any>): SolSelect<V> {
    return this.copy({ viewId }).calculateViewData();
  }

  public withViewOptions(viewOptions: Partial<SolViewOptions>): SolSelect<V> {
    return this.copy({ viewOptions: { ...viewOptions } }).calculateViewData();
  }

  public withValues(values: (string | undefined)[]): SolSelect<V> {
    const normalizedValues = SolSelect.normalizeValues(this.mode, values);
    return this.copy({ values: normalizedValues })
      .recalculateVisibleAvailableValues()
      .recalculateCurrentResultId()
      .recalculateActiveOptionId()
      .calculateViewData();
  }

  public withAvailableValues(availableValues: SolSelectValue<V>[], searchVersion?: number): SolSelect<V> {
    const nextAvailable = [...availableValues];
    const nextCached = SolSelect.mergeCache(this.cachedValues, nextAvailable);
    const shouldStopLoading =
      this.searchMode !== 'remote' || searchVersion === undefined ? true : searchVersion >= this.searchVersion;
    return this.copy({
      availableValues: nextAvailable,
      cachedValues: nextCached,
      loading: shouldStopLoading ? false : this.loading,
    })
      .recalculateVisibleAvailableValues()
      .recalculateCurrentResultId()
      .recalculateActiveOptionId()
      .calculateViewData();
  }

  public withLoading(loading: boolean): SolSelect<V> {
    return this.copy({ loading }).recalculateCurrentResultId().recalculateActiveOptionId();
  }

  public withSearchable(searchable: boolean): SolSelect<V> {
    return this.copy({ searchableRequested: searchable }).recalculateSearchable();
  }

  public withGrow(grow: boolean): SolSelect<V> {
    return this.copy({ grow });
  }

  public withHasError(hasError: boolean): SolSelect<V> {
    return this.copy({ hasError });
  }

  public withEditable(editable: boolean): SolSelect<V> {
    return this.copy({ editable })
      .recalculateVisibleAvailableValues()
      .recalculateCurrentResultId()
      .recalculateActiveOptionId();
  }

  /** Selects a value the option list does not carry; it stays listed only while it is selected. */
  public withAddedValue(value: string): SolSelect<V> {
    const added = value.trim();
    if (added.length === 0) {
      return this;
    }
    return this.mode === 'multi' ? this.withValues([...this.values, added]) : this.withValues([added]);
  }

  public withSearchLabels(availableValues: SolSelectValue<V>[]): SolSelect<V> {
    if (ObjectTools.equals(availableValues, this.availableValues)) {
      return this;
    }
    return this.copy({ availableValues })
      .recalculateVisibleAvailableValues()
      .recalculateCurrentResultId()
      .recalculateActiveOptionId();
  }

  public withDebounceMs(debounceMs: number): SolSelect<V> {
    return this.copy({ debounceMs: Math.max(0, debounceMs) });
  }

  public withMinChars(minChars: number): SolSelect<V> {
    return this.copy({ minChars: Math.max(0, minChars) });
  }

  public withDisabled(disabled: boolean): SolSelect<V> {
    return this.copy({ disabled });
  }

  public withPlaceholder(placeholder: SolText): SolSelect<V> {
    return this.copy({ placeholder });
  }

  public withTransparent(transparent: boolean): SolSelect<V> {
    if (this.transparent === transparent) return this;
    return this.copy({ transparent });
  }

  public withMenu(menu: SolMenuGroup[]): SolSelect<V> {
    return this.copy({ menu });
  }

  public withListboxId(listboxId: string | undefined): SolSelect<V> {
    return this.copy({ listboxId }).recalculateActiveOptionId();
  }

  public withCurrentResultId(currentResultId: string | undefined): SolSelect<V> {
    return this.copy({ currentResultId }).recalculateCurrentResultId().recalculateActiveOptionId();
  }

  public withPopoverVisible(popoverVisible: boolean): SolSelect<V> {
    if (this.presentation === 'embedded') {
      return this;
    }
    const visible = this.disabled ? false : popoverVisible;
    if (this.popoverVisible === visible) {
      return this;
    }
    if (!visible) {
      return this.copy({
        popoverVisible: false,
        loading: false,
        currentResultId: undefined,
      }).recalculateActiveOptionId();
    }
    return this.copy({ popoverVisible: true });
  }

  public withClickField(): SolSelect<V> {
    return this.withPopoverVisible(!this.popoverVisible);
  }

  public withArrowMove(step: 1 | -1): SolSelect<V> {
    if (this.disabled) {
      return this;
    }
    return this.moveHighlight(step).withPopoverVisible(true);
  }

  public withEnter(): SolSelect<V> {
    if (this.disabled) {
      return this;
    }
    if (!this.popoverVisible && this.presentation === 'dropdown') {
      return this.withPopoverVisible(true);
    }
    if (this.currentResultId === undefined) {
      return this;
    }
    return this.toggleSelection(this.currentResultId).withPopoverVisible(false);
  }

  public withSpace(): SolSelect<V> {
    if (this.disabled) {
      return this;
    }
    if (!this.popoverVisible && this.presentation === 'dropdown') {
      return this.withPopoverVisible(true);
    }
    if (this.currentResultId === undefined) {
      return this;
    }
    const toggled = this.toggleSelection(this.currentResultId);
    const shouldClose = toggled.mode === 'single';
    return shouldClose ? toggled.withPopoverVisible(false) : toggled.withPopoverVisible(true);
  }

  public withEscape(): SolSelect<V> {
    return this.withPopoverVisible(false);
  }

  public withEdgeMove(edge: SolSelectEdge): SolSelect<V> {
    if (this.disabled) {
      return this;
    }
    return this.moveHighlightToEdge(edge).withPopoverVisible(true);
  }

  public withClearSelection(): SolSelect<V> {
    if (this.disabled) {
      return this;
    }
    return this.clear().withPopoverVisible(false);
  }

  public withSelect(id: string | undefined, close: boolean): SolSelect<V> {
    if (this.disabled) {
      return this;
    }
    const next = this.toggleSelection(id);
    return close ? next.withPopoverVisible(false) : next;
  }

  public withSearch(query: string): SolSelect<V> {
    if (this.disabled || query === this.query) {
      return this;
    }
    const shouldSearch = query.length === 0 || query.length >= this.minChars;
    const loading = this.searchMode === 'remote' && shouldSearch;
    const searchVersion = loading ? this.searchVersion + 1 : this.searchVersion;
    return this.copy({ searchVersion }).withQuery(query).withLoading(loading);
  }

  public canSearch(): boolean {
    return !this.disabled && (this.query.length === 0 || this.query.length >= this.minChars);
  }

  public shouldCloseOnSelect(): boolean {
    return this.mode === 'single';
  }

  public canSelect(id: string | undefined): boolean {
    if (this.loading) {
      return false;
    }
    const candidate = this.visibleAvailableValues.find(v => v.id === id);
    return candidate?.disabled !== true;
  }

  public calculateViewData(): SolSelect<V> {
    const values = this.values;
    const isEmptySelection = values.length === 1 && values[0] === undefined;
    const selectedValues: SolSelectValue<V>[] = [];
    if (!isEmptySelection) {
      for (const id of values) {
        const match = this.findValueById(id);
        if (match !== undefined) {
          selectedValues.push(match);
        } else if (id !== undefined) {
          selectedValues.push({ id, name: id as unknown as V });
        }
      }
    }
    const selectedList = selectedValues.map(value => value.name);
    const selectedViewData: SolViewData<V[], SolViewOptions> = {
      dataType: this.dataType.array,
      viewId: this.viewId ?? SolViewId.tag,
      mode: 'read',
      value: selectedList,
      viewOptions: SolSelect.buildViewOptions(this.viewOptions),
    };
    const hasSelection = selectedList.length > 0;
    const showClear = hasSelection;
    return this.copy({ selectedViewData, hasSelection, showClear });
  }

  public toggleSelection(id: string | undefined): SolSelect<V> {
    if (!this.canSelect(id)) {
      return this;
    }

    if (this.mode === 'single') {
      return this.withValues([id]);
    }

    const exists = this.values.includes(id);
    const nextValues = exists ? this.values.filter(v => v !== id) : [...this.values, id];
    return this.withValues(nextValues);
  }

  public moveHighlight(step: 1 | -1): SolSelect<V> {
    const enabledIds = this.enabledResultIds();
    if (enabledIds.length === 0) {
      return this.copy({ currentResultId: undefined }).recalculateActiveOptionId();
    }
    const currentIndex = this.currentResultId ? enabledIds.indexOf(this.currentResultId) : -1;
    const nextIndex = this.nextIndex(enabledIds.length, currentIndex, step);
    return this.copy({ currentResultId: enabledIds[nextIndex] }).recalculateActiveOptionId();
  }

  private moveHighlightToEdge(edge: SolSelectEdge): SolSelect<V> {
    const enabledIds = this.enabledResultIds();
    if (enabledIds.length === 0) {
      return this.copy({ currentResultId: undefined }).recalculateActiveOptionId();
    }
    const id = edge === 'first' ? enabledIds[0] : enabledIds[enabledIds.length - 1];
    return this.copy({ currentResultId: id }).recalculateActiveOptionId();
  }

  private enabledResultIds(): string[] {
    if (this.loading) {
      return [];
    }
    return this.visibleAvailableValues.filter(v => !v.disabled && v.id !== undefined).map(v => v.id as string);
  }

  private recalculateVisibleAvailableValues(): SolSelect<V> {
    const candidates = this.withAddedValueOptions();
    const query = this.searchQuery();
    if (!this.filtersLocally() || query.length === 0) {
      return this.copy({ visibleAvailableValues: candidates });
    }
    const visibleAvailableValues = candidates.filter(value =>
      (value.searchLabel ?? value.id ?? '').toLowerCase().includes(query),
    );
    return this.copy({ visibleAvailableValues });
  }

  /** Surrounding whitespace is not part of what the reader is looking for, and a query of only spaces is no query. */
  private searchQuery(): string {
    return this.query.trim().toLowerCase();
  }

  /**
   * An editable select can hold a value the option list does not carry. It is listed like any other
   * option so it can be seen and deselected, and it leaves again the moment it stops being selected.
   */
  private withAddedValueOptions(): SolSelectValue<V>[] {
    if (!this.editable) {
      return this.availableValues;
    }
    const added = this.values.filter(
      (value): value is string => value !== undefined && !this.availableValues.some(option => option.id === value),
    );
    if (added.length === 0) {
      return this.availableValues;
    }
    const addedOptions = added.map(value => ({ id: value, name: value as unknown as V, searchLabel: value }));
    return [...addedOptions, ...this.availableValues];
  }

  private recalculateCurrentResultId(): SolSelect<V> {
    const stillVisible = this.visibleAvailableValues.some(
      value => value.id !== undefined && value.id === this.currentResultId && !value.disabled,
    );
    const currentResultId = stillVisible ? this.currentResultId : this.uniqueMatchId();
    return this.copy({ currentResultId });
  }

  private uniqueMatchId(): string | undefined {
    if (!this.filtersLocally() || this.mode !== 'single' || this.searchQuery().length === 0) {
      return undefined;
    }
    const enabledIds = this.enabledResultIds();
    return enabledIds.length === 1 ? enabledIds[0] : undefined;
  }

  private recalculateSearchable(): SolSelect<V> {
    return this.copy({ searchable: this.searchMode === 'remote' || this.searchableRequested });
  }

  /** A remote select never filters: the server can match a field the option's label does not show, so narrowing its answer here would drop rows that do match. */
  private filtersLocally(): boolean {
    return this.searchMode === 'local';
  }

  private recalculateActiveOptionId(): SolSelect<V> {
    return this.copy({ activeOptionId: SelectTools.toOptionId(this.listboxId, this.currentResultId) });
  }

  private findValueById(id: string | undefined): SolSelectValue<V> | undefined {
    if (id === undefined) {
      return undefined;
    }
    return this.availableValues.find(value => value.id === id) ?? this.cachedValues.find(value => value.id === id);
  }

  private nextIndex(length: number, currentIndex: number, step: 1 | -1): number {
    const next = currentIndex + step;
    if (next < 0) {
      return length - 1;
    }
    if (next >= length) {
      return 0;
    }
    return next;
  }

  private copy(props: Partial<SolSelect<V>>): SolSelect<V> {
    return ObjectTools.copy<SolSelect<V>>(this, props);
  }

  /** Each read view owns its compact rendering inside the one-line trigger. */
  private static buildViewOptions(viewOptions: Partial<SolViewOptions>): Partial<SolViewOptions> {
    const layout = SolLayout.of().withType('horizontal').withGap('8').withAlignX('left');
    return { ...viewOptions, layout, compact: true };
  }

  private static normalizeValues(mode: SolSelectMode, values: (string | undefined)[]): (string | undefined)[] {
    const baseValues = mode === 'single' ? SolSelect.toSingleValue(values) : values.filter(v => v !== undefined);
    return SolSelect.dedupe(baseValues);
  }

  private static toSingleValue(values: (string | undefined)[]): (string | undefined)[] {
    const first = values.length === 0 ? undefined : values[0];
    return [first];
  }

  private static dedupe(values: (string | undefined)[]): (string | undefined)[] {
    const seen = new Set<string | undefined>();
    const result: (string | undefined)[] = [];
    for (const value of values) {
      if (seen.has(value)) {
        continue;
      }
      seen.add(value);
      result.push(value);
    }
    return result;
  }

  private static mergeCache<V>(cached: SolSelectValue<V>[], incoming: SolSelectValue<V>[]): SolSelectValue<V>[] {
    const result: SolSelectValue<V>[] = [...cached];
    for (const value of incoming) {
      const index = result.findIndex(v => v.id === value.id);
      if (index === -1) {
        result.push(value);
      } else {
        result[index] = value;
      }
    }
    return result;
  }
}
